/**
 * PortalBerechtigungenPanel — the broker-cockpit surface for Bereich 6
 * (Kundenportal-Berechtigungsgruppen, Berechtigungskonzept V4.0 §8).
 *
 * Mounted in the existing 1-click portal-invite surface (Administration ›
 * Portal-Zugänge) once a customer subject is resolved. It hosts, top to bottom:
 *   1. the Einfach | Gruppenbasiert mode toggle (+ explanation),
 *   2. (group-mode only) the Berechtigungsgruppen list + editor,
 *   3. the portal invite (gated on >=1 group in group-mode),
 *   4. the primary holder (portal_access) + the portal grants list + revoke.
 *
 * All reads/writes go through the Bereich-6 api layer, which does the live/mock
 * split — so the whole panel works standalone in dev + tests.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import { StatusDot } from '../../components/ui/StatusDot';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { dateShortDe } from '../../lib/format';
import { LoadingState, ErrorState, EmptyState, Button } from '../administration/components';
import { usePortalAccess, usePortalGrants } from '../administration/hooks';
import { PortalModeToggle } from './components/PortalModeToggle';
import { PortalGruppenList } from './components/PortalGruppenList';
import { PortalInviteForm } from './components/PortalInviteForm';
import {
  useIgb2bKategorien,
  usePortalGruppen,
  usePortalMemberPool,
  usePortalVisibilityMode,
  useSetPortalVisibilityMode,
} from './hooks';
import type { PortalVisibilityMode } from './types';

/** portal_access.status → the row's status dot. Presentation only; the api owns the value. */
const HOLDER_STATUS: Record<string, { tone: 'success' | 'info' | 'neutral'; label: string }> = {
  active:      { tone: 'success', label: 'Aktiv' },
  invited:     { tone: 'info',    label: 'Eingeladen' },
  not_invited: { tone: 'neutral', label: 'Nicht eingeladen' },
};

interface Props {
  subjectType: 'person' | 'firma';
  subjectId: string;
  subjectName?: string;
  /** May toggle the mode + create/edit/delete groups (broker-gated). */
  canManageGroups: boolean;
  /** May send portal invites. */
  canInvite: boolean;
  /** May revoke standing portal grants. */
  canRevoke: boolean;
}

export function PortalBerechtigungenPanel({
  subjectType,
  subjectId,
  subjectName,
  canManageGroups,
  canInvite,
  canRevoke,
}: Props) {
  const qc = useQueryClient();
  const [feedback, setFeedback] = useState<string | null>(null);
  const showFeedback = (msg: string) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(null), 2500);
  };

  const modeQuery = usePortalVisibilityMode(subjectType, subjectId);
  const mode: PortalVisibilityMode = modeQuery.data?.mode ?? 'einfach';
  const isGroupMode = mode === 'gruppenbasiert';

  const setMode = useSetPortalVisibilityMode(subjectType, subjectId);

  const gruppenQuery = usePortalGruppen(subjectType, subjectId);
  const gruppen = gruppenQuery.data?.groups ?? [];

  const memberPoolQuery = usePortalMemberPool(subjectType, subjectId, isGroupMode);
  const memberPool = memberPoolQuery.data ?? [];

  const kategorienQuery = useIgb2bKategorien(isGroupMode);
  const kategorien = kategorienQuery.data ?? [];

  const grantsQuery = usePortalGrants(subjectType, subjectId, true);
  // The PRIMARY holder lives in portal_access, not in portal_grant; without this read the
  // person the 1-click invite actually invites is invisible in the list below.
  const accessQuery = usePortalAccess(subjectType, subjectId, true);
  const holder = accessQuery.data?.access ?? null;
  const revoke = useMutation({
    mutationFn: (grantId: string) => apiClient.admin.revokePortalGrant(grantId),
    onSuccess: () => {
      showFeedback('Zugang widerrufen.');
      void qc.invalidateQueries({ queryKey: qk.adminPortalGrants(subjectType, subjectId) });
    },
  });

  if (modeQuery.isLoading) return <LoadingState label="Portal-Berechtigungen werden geladen …" />;
  if (modeQuery.isError) return <ErrorState />;

  return (
    <div className="flex flex-col gap-6">
      {feedback && (
        <div
          role="status"
          aria-live="polite"
          className="rounded-md border border-status-success-border bg-status-success-bg px-3 py-2 text-sm text-status-success"
        >
          {feedback}
        </div>
      )}

      {/* 1. Mode toggle + feature explanation */}
      <section className="flex flex-col gap-3">
        <PortalModeToggle
          mode={mode}
          onChange={(next) => setMode.mutate(next)}
          disabled={!canManageGroups}
          pending={setMode.isPending}
        />
        <p className="text-xs text-muted-foreground">
          Im gruppenbasierten Modus richtet sich die Sichtbarkeit nach der Versicherungskategorie
          (IGB2B-Branchencode) jeder Police. Verbundene Dokumente, Rechnungen und Schäden erben die
          Sichtbarkeit ihrer Police. Beispiel: Personenversicherungen mit Lohndaten nur für HR
          sichtbar machen.
        </p>
        {!canManageGroups && (
          <p className="text-xs text-muted-foreground">
            Für Änderungen an Modus und Gruppen fehlt Ihnen die Berechtigung.
          </p>
        )}
      </section>

      {/* 2. Groups (group-mode only) */}
      {isGroupMode && (
        <section>
          {gruppenQuery.isLoading || kategorienQuery.isLoading || memberPoolQuery.isLoading ? (
            <LoadingState label="Gruppen werden geladen …" />
          ) : gruppenQuery.isError ? (
            <ErrorState />
          ) : (
            <PortalGruppenList
              subjectType={subjectType}
              subjectId={subjectId}
              gruppen={gruppen}
              memberPool={memberPool}
              kategorien={kategorien}
              canManage={canManageGroups}
              onFeedback={showFeedback}
            />
          )}
        </section>
      )}

      {/* 3. Invite (gated on >=1 group in group-mode) */}
      {canInvite && (
        <section>
          <PortalInviteForm
            subjectType={subjectType}
            subjectId={subjectId}
            mode={mode}
            gruppen={gruppen}
            onFeedback={showFeedback}
          />
        </section>
      )}

      {/* 4. Existing portal grants + revoke */}
      <section>
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
          <ShieldCheck className="size-4 text-muted-foreground" />
          Bestehende Zugänge{subjectName ? ` - ${subjectName}` : ''}
        </div>
        {grantsQuery.isLoading || accessQuery.isLoading ? (
          <LoadingState />
        ) : grantsQuery.isError || accessQuery.isError ? (
          <ErrorState />
        ) : holder === null && (grantsQuery.data ?? []).length === 0 ? (
          <EmptyState label="Keine Portal-Zugänge für diese Kundin / diesen Kunden." />
        ) : (
          <ul className="flex flex-col gap-2">
            {holder !== null && (
              <li
                key={holder.id}
                className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    Hauptzugang
                    {holder.invite_email != null && (
                      <span className="truncate font-normal text-muted-foreground">
                        {holder.invite_email}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <StatusDot
                      tone={HOLDER_STATUS[holder.status]?.tone ?? 'neutral'}
                      label={HOLDER_STATUS[holder.status]?.label ?? holder.status}
                    />
                    {holder.invited_at != null && (
                      <span className="text-xs text-muted-foreground">
                        Eingeladen am {dateShortDe(holder.invited_at)}
                      </span>
                    )}
                    {holder.activated_at != null && (
                      <span className="text-xs text-muted-foreground">
                        Aktiviert am {dateShortDe(holder.activated_at)}
                      </span>
                    )}
                  </div>
                </div>
              </li>
            )}
            {(grantsQuery.data ?? []).map((g) => {
              const revoked = g.revoked_at != null;
              return (
                <li
                  key={g.id}
                  className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-4 py-3"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      {g.role_label}
                      {g.scopes.length > 0 && (
                        <Badge variant="neutral">{g.scopes.length} Rechte</Badge>
                      )}
                    </div>
                    <div className="mt-0.5">
                      <StatusDot
                        tone={revoked ? 'neutral' : g.activated_at != null ? 'success' : 'info'}
                        label={
                          revoked ? 'Widerrufen' : g.activated_at != null ? 'Aktiv' : 'Eingeladen'
                        }
                      />
                    </div>
                  </div>
                  {canRevoke && !revoked && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-status-danger hover:bg-status-danger-bg"
                      disabled={revoke.isPending}
                      onClick={() => revoke.mutate(g.id)}
                    >
                      Widerrufen
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
